/**
 * Frontend subscription manager - prevents duplicate subscribe messages.
 * When multiple charts need the same stream, we refCount and only send
 * subscribe when first, unsubscribe when last.
 */

const activeSubscriptions = new Map(); // streamKey -> refCount

function getStreamKey(channel, params) {
  switch (channel) {
    case 'bars': {
      const session = params.sessionTemplate || params.sessiontemplate || 'Default';
      const conn = params.connectionId || '';
      const adapter = params.adapterType || '';
      return `bars:${params.symbol}|${params.timeframe}|${params.unit || 'Minute'}|${session}|${conn}|${adapter}`;
    }
    case 'marketAggregates':
      return `marketAggregates:${params.symbol}`;
    case 'orderFlow':
      return `orderFlow:${params.symbol}`;
    case 'optionChain': {
      const underlying = params.underlying || params.symbol || '';
      const expiration = params.expiration || '';
      const proximity = params.strikeProximity != null ? params.strikeProximity : '';
      const conn = params.connectionId || '';
      return `optionChain:${underlying}|${expiration}|${proximity}|${conn}`;
    }
    case 'positions':
      return `positions:${params.accountId}|${params.paperTrading ? 1 : 0}`;
    case 'orders':
      return `orders:${params.accountId}|${params.paperTrading ? 1 : 0}`;
    default:
      return `${channel}:${JSON.stringify(params)}`;
  }
}

export function subscribe(sendFn, channel, params) {
  const key = getStreamKey(channel, params);
  const count = activeSubscriptions.get(key) || 0;
  activeSubscriptions.set(key, count + 1);
  if (channel === 'bars' && process.env.NODE_ENV === 'development') {
    console.debug('[wsSubscriptionManager] subscribe bars:', params.symbol, params.timeframe, `refCount: ${count} → ${count + 1}`, count === 0 ? '(SENDING)' : '(skipped, already subscribed)');
  }
  if (count === 0) {
    sendFn({ type: 'subscribe', channel, ...params });
  }
}

export function unsubscribe(sendFn, channel, params) {
  const key = getStreamKey(channel, params);
  const count = activeSubscriptions.get(key) || 0;
  if (channel === 'bars' && process.env.NODE_ENV === 'development') {
    console.debug('[wsSubscriptionManager] unsubscribe bars:', params.symbol, params.timeframe, `refCount: ${count} → ${count <= 1 ? 0 : count - 1}`, count <= 1 ? '(SENDING)' : '(skipped, still has refs)');
  }
  if (count <= 1) {
    activeSubscriptions.delete(key);
    sendFn({ type: 'unsubscribe', channel, ...params });
  } else {
    activeSubscriptions.set(key, count - 1);
  }
}

/**
 * Clear refCount without sending unsubscribe. Use when the connection is lost
 * (e.g. tab hidden, WebSocket reconnecting) - we're not intentionally unsubscribing,
 * so we don't send unsubscribe. On reconnect, subscribe will send subscribe (idempotent).
 */
export function clearRefCountOnDisconnect(channel, params) {
  const key = getStreamKey(channel, params);
  activeSubscriptions.delete(key);
}
